import { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/feature/Logo";
import { supabase } from "@/lib/supabase";
import { useAdminI18n, type AdminLang } from "@/hooks/useAdminI18n";

const langOptions: { value: AdminLang; label: string; flag: string }[] = [
  { value: "de", label: "Deutsch", flag: "🇩🇪" },
  { value: "tr", label: "Türkçe", flag: "🇹🇷" },
];

export default function AdminForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const { lang, setLang, t } = useAdminI18n();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);

    try {
      // redirectTo must point to the reset-password page so Supabase
      // appends the access_token hash fragment there after the user clicks the link.
      const redirectTo = `${window.location.origin}/admin/passwort-zuruecksetzen`;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });

      if (error) {
        setErrorMsg(t("forgotPassword.error.generic"));
      } else {
        // Always show success — never reveal whether the email exists
        setSent(true);
      }
    } catch {
      setErrorMsg(t("forgotPassword.error.generic"));
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-hj-gray-light flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Language switcher */}
        <div className="flex justify-end mb-3 gap-1">
          {langOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setLang(opt.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                lang === opt.value
                  ? "border-hj-red text-hj-red bg-hj-red/5"
                  : "border-hj-gray-border text-hj-gray hover:bg-white"
              }`}
            >
              <span>{opt.flag}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-hj-gray-border p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex mb-4">
              <Logo variant="header" />
            </div>
            <h1 className="text-2xl font-bold text-hj-dark">
              {t("forgotPassword.title")}
            </h1>
            <p className="text-sm text-hj-gray mt-1">
              {t("forgotPassword.subtitle")}
            </p>
          </div>

          {/* Success state */}
          {sent ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-md flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                  <i className="ri-checkbox-circle-line text-green-500 text-lg" />
                </div>
                <div>
                  <p className="text-sm font-medium text-green-800">
                    {t("forgotPassword.success.title")}
                  </p>
                  <p className="text-sm text-green-700 mt-1 leading-relaxed">
                    {t("forgotPassword.success.message")}
                  </p>
                </div>
              </div>
              <Link
                to="/admin/login"
                className="flex items-center justify-center gap-2 w-full py-2.5 border border-hj-gray-border text-hj-dark text-sm font-medium rounded-md hover:bg-hj-gray-light transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm" />
                </div>
                {t("forgotPassword.backToLogin")}
              </Link>
            </div>
          ) : (
            <>
              {/* Error message */}
              {errorMsg && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-error-warning-line text-red-500 text-sm" />
                  </div>
                  <p className="text-sm text-red-600">{errorMsg}</p>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-hj-dark mb-1.5">
                    {t("forgotPassword.email")}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                    placeholder="admin@example.com"
                    required
                    autoComplete="email"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-hj-red text-white font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin text-sm" />
                      {t("forgotPassword.submitting")}
                    </span>
                  ) : (
                    t("forgotPassword.submit")
                  )}
                </button>
              </form>

              {/* Back to login */}
              <div className="mt-5 text-center">
                <Link
                  to="/admin/login"
                  className="inline-flex items-center gap-1.5 text-sm text-hj-gray hover:text-hj-dark transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-sm" />
                  </div>
                  {t("forgotPassword.backToLogin")}
                </Link>
              </div>
            </>
          )}

          <div className="mt-6 pt-4 border-t border-hj-gray-border text-center">
            <p className="text-[11px] text-gray-400">
              {t("footer.developedBy")}{" "}
              <a
                href="https://kreativweb.ch"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-hj-red transition-colors"
              >
                KreativWeb.ch
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
